import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useBooking } from '../../contexts/booking/BookingContext';
import { useAuth } from '../../contexts/auth/AuthContext';
import { formatPrice, calculatePricingBreakdown, PricingBreakdown } from '../../utils/bookingUtils';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Props for PaymentFormInner
interface PaymentFormInnerProps {
  pricing: PricingBreakdown;
  onWalletPayment: () => Promise<void>;
}

// Inner component that has access to Stripe Elements
const PaymentFormInner: React.FC<PaymentFormInnerProps> = ({ pricing, onWalletPayment }) => {
  const { bookingState, prevStep, nextStep, getTotalPrice, setPaymentIntent, setPaymentStatus, saveBookingState } = useBooking();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'wallet'>('card');

  const stripe = useStripe();
  const elements = useElements();
  const totalAmount = pricing.afterDiscount; // Use discounted amount
  

  // Handle Pay Later functionality
  const handlePayLater = async () => {
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ payment_status: 'pending_payment' })
        .eq('id', bookingState.appointmentId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      setPaymentStatus('pending');
      setPaymentIntent('pay_later');
      nextStep();
    } catch (err: any) {
      console.error('Pay Later error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    setIsProcessing(true);
    setErrorMessage(null);
    
    try {
      // Save booking state to localStorage before payment (for Afterpay redirects)
      saveBookingState();
      
      // Confirm payment with redirect support for Afterpay
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/booking?step=confirmation&appointment_id=${bookingState.appointmentId}`,
          payment_method_data: {
            billing_details: {
              name: `${bookingState.customerInfo?.firstName} ${bookingState.customerInfo?.lastName}`,
              email: bookingState.customerInfo?.email,
            },
          },
        },
        redirect: 'if_required', // Only redirect for payment methods that require it (like Afterpay)
      });
      
      if (error) {
        // Payment failed or was cancelled
        setErrorMessage(error.message || 'Payment failed');
        setPaymentStatus('failed');
        setIsProcessing(false);
      } else {
        // Payment succeeded without redirect (e.g., card payment)
        setPaymentStatus('succeeded');
        
        // Move to confirmation step (webhook will confirm payment)
        nextStep();
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
      setPaymentStatus('failed');
      setIsProcessing(false);
    }
  };
  
  // Handle wallet payment
  const handleWalletPayment = async () => {
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      await onWalletPayment();
    } catch (err: any) {
      setErrorMessage(err.message || 'Wallet payment failed');
      setIsProcessing(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Payment</h2>
      <p className="text-gray-600 mb-6">Please select your payment method to complete your booking</p>

      {/* Payment summary with discount breakdown */}
      <div className="bg-[#F1F7F4] p-4 rounded-md mb-6">
        <h3 className="font-medium text-[#232347] mb-3">Payment Summary</h3>

        {/* Subtotal */}
        <div className="flex justify-between text-gray-700">
          <span>Subtotal:</span>
          <span>${formatPrice(pricing.subtotal)}</span>
        </div>

        {/* Member Discount */}
        {pricing.memberDiscountAmount > 0 && (
          <div className="flex justify-between text-green-600 mt-1">
            <span>Member Discount ({pricing.memberDiscountPercent}%):</span>
            <span>-${formatPrice(pricing.memberDiscountAmount)}</span>
          </div>
        )}

        {/* Total */}
        <div className="flex justify-between font-medium text-[#232347] mt-2 pt-2 border-t border-gray-200">
          <span>Total:</span>
          <span>${formatPrice(pricing.afterDiscount)}</span>
        </div>

        {/* Wallet Balance */}
        {pricing.walletBalance > 0 && (
          <div className="flex justify-between text-blue-600 mt-2 pt-2 border-t border-gray-200">
            <span>Wallet Balance:</span>
            <span>${formatPrice(pricing.walletBalance)}</span>
          </div>
        )}
      </div>

      {/* Payment Method Selection (if wallet available) */}
      {pricing.canPayWithWallet && (
        <div className="mb-6">
          <label className="block text-[#232347] mb-3 font-medium">Choose Payment Method</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setPaymentMethod('wallet')}
              className={`p-4 rounded-md border-2 text-left transition-colors ${
                paymentMethod === 'wallet'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="font-medium">Wallet</span>
              </div>
              <p className="text-sm text-gray-600">Pay with your ${formatPrice(pricing.walletBalance)} balance</p>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod('card')}
              className={`p-4 rounded-md border-2 text-left transition-colors ${
                paymentMethod === 'card'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="font-medium">Card / Afterpay</span>
              </div>
              <p className="text-sm text-gray-600">Pay with card or split payments</p>
            </button>
          </div>
        </div>
      )}

      {/* Card Payment Form */}
      {(paymentMethod === 'card' || !pricing.canPayWithWallet) && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[#232347] mb-2">Payment Method</label>
            <div className="bg-white">
              <PaymentElement />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Your payment information is secure and encrypted.
            </p>
            <p className="text-sm text-[#3A5E63] mt-1">
              💳 Pay with card or split into 4 interest-free payments with Afterpay
            </p>
          </div>

          {errorMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
              {errorMessage}
            </div>
          )}

          {/* Information banner for Pay Later option */}
          <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 p-3 rounded-md">
            <p className="text-sm">
              <strong>Payment Options:</strong> You can pay now with a card or choose "Pay Later" to complete payment at the spa.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8">
            <button
              type="button"
              onClick={prevStep}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
            >
              Back
            </button>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handlePayLater}
                disabled={isProcessing}
                className="px-6 py-2 rounded-md border-2 border-[#3A5E63] text-[#3A5E63] hover:bg-[#3A5E63] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-[#3A5E63] border-t-transparent rounded-full mr-2"></span>
                    Processing...
                  </>
                ) : (
                  'Pay Later'
                )}
              </button>

              <button
                type="submit"
                disabled={!stripe || !elements || isProcessing}
                className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Processing...
                  </>
                ) : (
                  `Pay $${formatPrice(totalAmount)}`
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Wallet Payment Section */}
      {paymentMethod === 'wallet' && pricing.canPayWithWallet && (
        <div className="space-y-6">
          {errorMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
              {errorMessage}
            </div>
          )}

          <div className="bg-blue-50 p-4 rounded-md">
            <div className="flex items-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium text-blue-800">Pay with Wallet</span>
            </div>
            <p className="text-blue-700">
              ${formatPrice(pricing.afterDiscount)} will be deducted from your wallet balance.
            </p>
            <p className="text-sm text-blue-600 mt-1">
              Remaining balance after payment: ${formatPrice(pricing.walletBalance - pricing.afterDiscount)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <button
              type="button"
              onClick={prevStep}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleWalletPayment}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                `Pay $${formatPrice(pricing.afterDiscount)} with Wallet`
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Outer component that creates payment intent and wraps with Elements
const PaymentForm: React.FC = () => {
  const { bookingState, getTotalPrice, setPaymentStatus, setPaymentIntent, nextStep } = useBooking();
  const { isMember, userProfile, refreshUserProfile } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const creationInitiatedRef = useRef(false);

  // Calculate pricing with member discount
  const walletBalance = userProfile?.wallet_balance || 0;
  const pricing = calculatePricingBreakdown(bookingState.selectedServices, isMember, walletBalance);
  const totalAmount = pricing.afterDiscount;
  
  // Create payment intent on mount
  useEffect(() => {
    const createPaymentIntent = async () => {
      if (creationInitiatedRef.current) {
        return;
      }
      creationInitiatedRef.current = true;

      try {
        setIsLoading(true);

        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        };

        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CREATE_PAYMENT_INTENT),
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              amount: totalAmount,
              appointment_id: bookingState.appointmentId,
              services: bookingState.selectedServices.map(service => ({
                id: service.id,
                name: service.name
              })),
              // Include discount info for tracking
              original_amount: pricing.subtotal,
              member_discount_amount: pricing.memberDiscountAmount,
              member_discount_percent: pricing.memberDiscountPercent,
              is_member: isMember,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage;
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error?.message || `Error ${response.status}`;
          } catch (e) {
            errorMessage = `Error ${response.status}: ${errorText}`;
          }
          throw new Error(errorMessage);
        }

        const { clientSecret } = await response.json();
        if (!clientSecret) {
          throw new Error('No client secret returned from server');
        }

        setClientSecret(clientSecret);
        setPaymentStatus('pending');
      } catch (error: any) {
        console.error('Error creating payment intent:', error);
        setError(error.message || 'Failed to initialize payment');
      } finally {
        setIsLoading(false);
      }
    };

    if (bookingState.appointmentId) {
      createPaymentIntent();
    } else {
      setError('Booking information is incomplete. Please go back and review your booking details.');
      setIsLoading(false);
    }
  }, [bookingState.appointmentId, bookingState.selectedServices, setPaymentStatus, totalAmount, pricing.subtotal, pricing.memberDiscountAmount, pricing.memberDiscountPercent, isMember]);

  // Handle wallet payment
  const handleWalletPayment = async () => {
    if (!bookingState.appointmentId || !userProfile?.id) {
      throw new Error('Missing required information for wallet payment');
    }

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Call wallet payment edge function
    const response = await fetch(
      edgeFunctionUrl(EdgeFunctions.USE_WALLET_PAYMENT),
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          appointment_id: bookingState.appointmentId,
          amount: pricing.afterDiscount,
          original_amount: pricing.subtotal,
          member_discount_amount: pricing.memberDiscountAmount,
          member_discount_percent: pricing.memberDiscountPercent,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Wallet payment failed');
    }

    // Update appointment status
    await supabase
      .from('appointments')
      .update({ payment_status: 'paid' })
      .eq('id', bookingState.appointmentId);

    // Refresh user profile to get updated wallet balance
    await refreshUserProfile();

    // Mark payment as successful
    setPaymentStatus('succeeded');
    setPaymentIntent('wallet_payment');
    nextStep();
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-12 w-12 border-4 border-[#B2D8C5] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Initializing payment...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (!clientSecret) {
    return null;
  }
  
  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#B2D8C5',
      },
    },
  };
  
  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentFormInner pricing={pricing} onWalletPayment={handleWalletPayment} />
    </Elements>
  );
};

export default PaymentForm;